import * as React from 'react'
import { QuestionDTO } from 'jobs/dtos/questions'
import BoolValue from './Bool/components/BoolValue'
import { ValueComponentProps, QuestionEditorComponentProps } from './props'
import BoolQuestionViewModel from './Bool/BoolQuestionViewModel'
import UnknownValue from './Unknown/components/UnknownValue'
import TextQuestionViewModel from './Text/TextQuestionViewModel'
import TextValue from './Text/components/TextValue'
import NumericQuestionViewModel from './Numeric/NumericQuestionViewModel'
import NumericValue from './Numeric/components/NumericValue'
import DateTimeQuestionViewModel from './DateTime/DateTimeQuestionViewModel'
import DateTimeValue from './DateTime/components/DateTimeValue'
import DocumentsQuestionViewModel from './Documents/DocumentsQuestionViewModel'
import DocumentsValue from './Documents/components/DocumentsValue'
import SelectQuestionViewModel from './Select/SelectQuestionViewModel'
import SelectValue from './Select/components/SelectValue'
import BreakerQuestionViewModel from './Breaker/BreakerQuestionViewModel'
import BreakerValue from './Breaker/components/BreakerValue'
import BoolEditorInput from './Bool/components/BoolEditorInput'
import TextEditorInput from './Text/components/TextEditorInput'
import { QuestionViewModelFor } from './type-registry'
import SelectEditorInput from './Select/components/SelectEditorInput'
import UnknownQuestionViewModel from './Unknown/UnknownQuestionViewModel'
import DateTimeEditorInput from './DateTime/components/DateTimeEditorInput'
import BreakerEditorInput from './Breaker/components/BreakerEditorInput'
import DocumentsEditorInput from './Documents/components/DocumentsEditorInput'
import NumericEditorInput from './Numeric/components/NumericEditorInput'

/**
 * Possible question view models.
 */
export type QuestionViewModel =
  | BoolQuestionViewModel
  | TextQuestionViewModel
  | NumericQuestionViewModel
  | DateTimeQuestionViewModel
  | DocumentsQuestionViewModel
  | SelectQuestionViewModel
  | BreakerQuestionViewModel

/**
 * Fallback.
 */
const unknownEntry: QuestionComponentRegistryEntry<any> = {
  ValueComponent: UnknownValue,
  EditorInputComponent: ((props: { vm: UnknownQuestionViewModel }) => (
    <div
      style={{
        backgroundColor: '#ffd3a7',
        padding: 10,
        color: '#9c5309',
        fontSize: 12,
        border: '1px solid #f1b375',
        borderRadius: 4,
      }}
    >
      Unknown question type "{props.vm.type.replace('_', ' ').toLowerCase()}" —
      hey, can't win them all ¯\_(ツ)_/¯
    </div>
  )) as any,
}

/**
 * Question registry.
 */
const questionComponentRegistry: QuestionComponentRegistry = {
  BOOL: {
    ValueComponent: BoolValue,
    EditorInputComponent: BoolEditorInput,
  },
  TEXT: {
    ValueComponent: TextValue,
    EditorInputComponent: TextEditorInput,
  },
  NUMERIC: {
    ValueComponent: NumericValue,
    EditorInputComponent: NumericEditorInput,
  },
  DATE_TIME: {
    ValueComponent: DateTimeValue,
    EditorInputComponent: DateTimeEditorInput,
  },
  DOCUMENTS: {
    ValueComponent: DocumentsValue,
    EditorInputComponent: DocumentsEditorInput,
  },
  SELECT: {
    ValueComponent: SelectValue,
    EditorInputComponent: SelectEditorInput,
  },
  BREAKER: {
    ValueComponent: BreakerValue,
    EditorInputComponent: BreakerEditorInput,
  },
}

/**
 * Safely gets a Question registry entry.
 * @param type
 */
export function getQuestionComponentRegistryEntry<T extends QuestionDTO>(
  type: T['type'],
): QuestionComponentRegistryEntry<QuestionDTO> {
  return questionComponentRegistry[type] || (unknownEntry as any)
}

/**
 * Registry entry.
 */
export interface QuestionComponentRegistryEntry<T extends QuestionDTO> {
  /**
   * Basic value component. Should preferably just return text.
   */
  ValueComponent: React.FC<
    React.PropsWithChildren<ValueComponentProps<QuestionViewModelFor<T>>>
  >
  /**
   * Mini editor component.
   */
  EditorInputComponent: React.FC<
    React.PropsWithChildren<
      QuestionEditorComponentProps<QuestionViewModelFor<T>>
    >
  >
}

/**
 * Question registry.
 */
export type QuestionComponentRegistry = {
  /**
   * This is TypeScript magic right here. Holy shit.
   */
  [P in QuestionDTO['type']]:
    | QuestionComponentRegistryEntry<Extract<QuestionDTO, { type: P }>>
    | undefined
}
